import { motion } from "framer-motion";
import { openContact } from "./ContactDrawer";
import { ArrowUpRight, PenTool } from "lucide-react";

const scrollToContact = (e) => {
  e.preventDefault();
  openContact("clothing_design");
};

const EASE = [0.16, 1, 0.3, 1];
const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: EASE } },
};

const SKILLS = [
  "Clothing-Ideen & Konzepte",
  "Handgezeichnete Skizzen",
  "Prints & Grafiken",
  "Logos & Designvarianten",
  "Produktideen",
  "Mockups & Visualisierungen",
];

export default function ClothingDesign() {
  return (
    <section id="clothing-design" data-testid="clothing-design-section" className="px-6 lg:px-12 py-24 lg:py-36 max-w-[1600px] mx-auto">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Text */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "100px" }}
          className="lg:col-span-5 flex flex-col gap-6"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold flex items-center gap-3">
            <PenTool size={14} /> Kapitel 02 — Clothing Design
          </p>
          <h2 className="font-display uppercase tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-5xl">
            Von der Idee zum{" "}
            <span className="font-editorial italic normal-case tracking-normal text-gold">Design.</span>
          </h2>
          <p className="text-base leading-relaxed text-zinc-500 max-w-lg">
            Du hast eine Clothing-Idee im Kopf? Ich bringe sie aufs Blatt. Jede Skizze
            entsteht von Hand — vom ersten Konstruktionsstrich über Silhouette und
            Details bis zum fertigen Design mit Prints, Logos und Farben. Was du auf
            dieser Seite siehst, ist echte Arbeit von mir — keine gekauften Templates.
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {SKILLS.map((s) => (
              <li key={s} className="flex items-start gap-3 text-[13px] leading-snug text-zinc-700">
                <span className="w-4 h-px bg-gold mt-2 shrink-0" />
                {s}
              </li>
            ))}
          </ul>
          <div>
            <a
              href="#kontakt"
              onClick={scrollToContact}
              data-testid="clothing-design-cta"
              className="group relative overflow-hidden inline-flex items-center gap-3 border border-ink text-ink px-8 py-4 text-xs uppercase tracking-[0.25em] hover:text-paper transition-colors duration-500"
            >
              <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              <span className="relative flex items-center gap-3">
                Design anfragen
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </a>
          </div>
        </motion.div>

        {/* Echte Skizzen als Diptychon */}
        <div className="lg:col-span-7 grid grid-cols-2 gap-4 md:gap-6">
          <motion.figure
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "80px" }}
            className="relative"
          >
            <div className="overflow-hidden border border-black/10 bg-[#0a0a0a]">
              <img
                src="/designs/outfit_b.webp"
                alt="Handgezeichnete Clothing-Skizze von Justin: Hoodie mit Cargo-Hose und Nieten-Details"
                loading="lazy"
                className="w-full aspect-[3/4] object-cover object-top transition-transform duration-700 hover:scale-[1.04]"
              />
            </div>
            <figcaption className="mt-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Signature Hoodie Set — Originalskizze
            </figcaption>
          </motion.figure>
          <motion.figure
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "80px" }}
            transition={{ delay: 0.15 }}
            className="relative mt-8 md:mt-14"
          >
            <div className="overflow-hidden border border-black/10 bg-[#0a0a0a]">
              <img
                src="/designs/outfit_a.webp"
                alt="Handgezeichnete Clothing-Skizze von Justin: Cap, Jacke und Hose mit Stern-Prints"
                loading="lazy"
                className="w-full aspect-[3/4] object-cover object-top transition-transform duration-700 hover:scale-[1.04]"
              />
            </div>
            <figcaption className="mt-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Cap · Jacke · Star Pants — Originalskizze
            </figcaption>
          </motion.figure>
        </div>
      </div>
    </section>
  );
}
